import { DatePipe } from '@angular/common';
import { AuthenticationService } from '../../services/authentication.service';
import { StringHelper } from '../../helpers/strings.helper';
import { AppConfig } from '../../../shared/config/app-config';
import { ApiClientHelper } from '../../helpers/api-client.helper';
import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationBehaviorOptions, Router } from '@angular/router';
import { WCL_PARENT_MODULE, WclParentModule } from '../working-capital-loans-v2.tokens';
import { StorageKeys } from '../../../shared/models/storage-keys';
import { MobileWebViewService } from '../../services/mobile-web-view.service';
import { ViewType } from '../../../shared/models/loan.model';
import { ReplaySubject, Subject, BehaviorSubject } from 'rxjs';
import { LocalStorageService } from '../../services/local-storage.service';
import { UserAlias } from '../../../shared/constants';
import moment from 'moment';
import { WclPortfolioManagementService } from './wcl-portfolio-management.service';
import {
    PortfolioManagementServiceError,
    Pageable,
    ParticipantCashout,
    IncomeCommissionsTransactionsParams,
    PayoutTransactionsHistoryParams,
    PortfolioListParams,
    LoanRequestListParams,
    PrequalificationListParams,
} from '../wcl-portfolio-management.model';
import { WalletService } from '../../services/wallet.service';
import { AccruedHistoryResponse } from '@mpt-fo/common/shared/models/overdraft.model';
import { LoanProduct } from './loan-analytics.service';

interface WclBoAccount {
    accountName: string;
    accountBalance: number;
    accountNumber?: string;
    accountNuban: string;
}

interface SubmitLoanOfferDecision {
    accepted: boolean;
    reason?: string;
    loanRequestRef: string;
}

interface PrimaryBusinessPayload {
    businessCode: string;
    businessTypeId: string;
}

interface LoanRequestState {
    loanRequestExists: boolean;
    isPrimaryBusinessSelected: boolean;
}

const loanProductToLabelMap = {
    [LoanProduct.OVERDRAFT]: 'Overdraft',
    [LoanProduct.AGENT_LOAN]: 'Agent Loan',
    [LoanProduct.WCL]: 'Working Capital Loan',
} as const;

export interface BorrowerLoanAgreement {
    requestStatus: string;
    borrowerAgreement: {
        url: string;
        status: string;
    };
}

export interface RejectionReasonsResponse {
    declineReasons: string[];
}

export enum GuarantorAgreementStatusEnum {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    SIGNED = 'SIGNED',
    COMPLETED = 'COMPLETED',
    EXPIRED = 'EXPIRED',
    REJECTED = 'REJECTED',
    FAILED = 'FAILED',
    CANCELLED = 'CANCELLED',
    AWAITING_AGREEMENT_DOCUMENT = 'AWAITING_AGREEMENT_DOCUMENT',
}

export interface LoanGuarantorAgreementsResponse {
    requestStatus: GuarantorAgreementRequestStatusEnum;
    guarantorAgreements: GuarantorAgreement[];
}
export type GuarantorStatus = GuarantorAgreementStatusEnum;
export interface GuarantorAgreement {
    name: string;
    agreementStatus: GuarantorStatus;
    url?: string;
}

export enum GuarantorAgreementRequestStatusEnum {
    DISPATCHED = 'DISPATCHED',
    CANCELLED = 'CANCELLED',
    PENDING = 'PENDING',
}

export interface FinalOfferDetailsResponse {
    loanProduct: string;
    loanAmount: number;
    interestRate: number;
    loanTenure: number;
    loanTenureUnit: string;
    managementFee: number;
    dateRequested: string;
    expiryDate: string;
    moratoriumInDays: number;
}

export interface LoanPrefillData {
    amount?: number;
    loanTenure?: number;
    loanTenureUnit?: string;
    repaymentFrequency?: string;
    loanProduct?: string;
    loanPurpose?: string;
}

export interface LoanRequestReApplicationResponse {
    loanRequestRef: string;
    loanProduct: string;
}

@Injectable({
    providedIn: 'root',
})
export class WclService {
    isPersonalLoan: boolean;
    searchParams: any;
    thresholdAmount: any;
    thresholdAmountSubject: Subject<any> = new ReplaySubject();
    requestModeValues = {
        SCRATCH: 'SCRATCH',
        RENEWAL: 'RENEWAL',
    };
    userRole: any;

    refresher$ = new Subject<number>();

    customHeaders = {
        'wcl-ui-flow': 'wcl-v1',
        // adding client-version here since it's not catered for by
        // apiClientHelper's getForPromise
        'client-version': AppConfig.CLIENT_VERSION,
    };

    constructor(
        @Optional() @Inject(WCL_PARENT_MODULE) private parentModule: WclParentModule,
        private apiClientHelper: ApiClientHelper,
        public config: AppConfig,
        private authService: AuthenticationService,
        private mobileService: MobileWebViewService,
        private router: Router,
        private walletService: WalletService,
        public localStorage: LocalStorageService,
        public portfolioManagementService: WclPortfolioManagementService,
        private datePipe: DatePipe,
    ) {
        this.userRole = this.localStorage.get(StorageKeys.USER_ROLE);

        this.customHeaders['wcl-client-platform'] = 'WEB';
        // if (this.supportsPlatformHeaderInjection) {
        // }
    }

    get context() {
        const context = this.authService.getContextUser();
        return context;
    }

    get useBORepaymentAccounts() {
        return this.config.USE_WCL_BUSINESS_OWNER_REPAYMENT_ACCOUNTS;
    }

    get useBODisbursementAccounts() {
        return this.config.USE_WCL_BUSINESS_OWNER_REPAYMENT_ACCOUNTS;
    }

    get useWclBusinessAddressVerificationV2() {
        return this.config.USE_WCL_BUSINESS_ADDRESS_VERIFICATION_V2;
    }

    get wclContractReference() {
        return localStorage.getItem(StorageKeys.loanContractRef);
    }

    set wclContractReferenceSet(reference: any) {
        localStorage.setItem(StorageKeys.loanContractRef, reference);
    }

    get checkViewType() {
        const loginParameter = this.mobileService.getMoniepointAppParameters();
        return this.mobileService.isMobileWebViewUser() ? loginParameter?.deviceRegistrationRequest?.model?.toUpperCase() : ViewType.WEB;
    }

    // TODO: make more investigations on this
    get routeParentUrl() {
        if (this.userRole) {
            switch (this.userRole) {
                case UserAlias.aggregator:
                    return `/main-app/aggregator`;
                case UserAlias.superAggregator:
                    return `/main-app/super-aggregator`;
                case UserAlias.supremeAggregator:
                    return `/main-app/supreme-aggregator`;
                default:
                    return `/main-app/aggregator`;
                    break;
            }
        } else {
            return `/main-app/aggregator`;
        }
    }

    get simpleEnglishDateFormat() {
        return "EEE, MMM d'TH', yyyy";
    }

    get platformHeaderIfSupported() {
        // if (!this.supportsPlatformHeaderInjection) return {};
        return { 'wcl-client-platform': 'WEB' };
    }

    getPrimerIntroductoryText(key) {
        return this.makeGetRequest(`/api/v1/wcl/introductory-information?key=${key}`);
    }

    getPrimerUses(key) {
        return this.makeGetRequest(`/api/v1/wcl/introductory-information?key=${key}`);
    }

    kycrequest(data: any) {
        return this.makePostRequest('/api/v1/wcl/request', data);
    }

    checkBOKycWCLEligibility(): Promise<{ code: number; message: string }> {
        return this.makeGetRequest('/api/v1/bo/validate-kyc');
    }

    getBusinessOnGoingLoanService() {
        if (this.config.USE_PENDING_LOAN_TIMELINE_V2) {
            return this.makeGetRequest('/api/v1/lms/request/ongoing').then((res) => {
                this.activeLoanProduct = res?.loanRequestResponse?.loanProduct;
                return res;
            });
        }
        return this.makeGetRequest('/api/v1/wcl/request/ongoing').then((res) => {
            this.activeLoanProduct = res?.loanRequestResponse?.loanProduct;
            return res;
        });
    }

    getBusinessApprovedLoanService() {
        return this.makeGetRequest('/api/v1/wcl/request/ongoing-approval');
    }

    checkifLoanPreviouslyGrantedService() {
        return this.makeGetRequest('/api/v1/wcl/loan/previously-granted-loan');
    }

    getKycInformationStatus(data: any) {
        return this.makePostRequest('/api/v1/wcl/kyc', data);
    }

    checkBOPersonalInformation(data: any) {
        return this.makePostRequest('/api/v1/wcl/kyc/business-owner/personal-info', data);
    }

    submitBOPersonalDetails(data: any) {
        return this.makePutRequest('/api/v1/wcl/kyc/business-owner/personal-info/submit', data);
    }

    submitBOBusinessTypeDetails(data: any) {
        return this.makePutRequest('/api/v1/wcl/kyc/business-type/submit', data);
    }

    submitBOBusinessAddressDetails(data: any) {
        return this.makePutRequest('/api/v1/wcl/kyc/business-address/submit', data);
    }

    submitBOBusinessAddressDetailsLoanRenewal(data: any) {
        return this.makePutRequest('/api/v1/wcl/renewal/confirmation/business-address', data);
    }

    submitBOResidentialAddressDetails(data: any) {
        return this.makePutRequest('/api/v1/wcl/kyc/business-owner/address/submit', data);
    }

    submitBOResidentialAddressDetailsLoanRenewal(data: any) {
        return this.makePutRequest('/api/v1/wcl/renewal/confirmation/residential-address', data);
    }

    searchBusinessType(keyword: string) {
        return this.makeGetRequest(`/api/v1/wcl/kyc/business/types?keywords=${keyword}`);
    }

    getBusinessTypes(params) {
        return this.makeGetRequest(`/api/v1/wcl/kyc/business/types/view`, params);
    }

    getBOBusinessTypeInformation(data: any) {
        return this.makePostRequest(`/api/v1/wcl/kyc/business-type`, data);
    }

    getBOResidentialAddressInformation(data: any) {
        return this.makePostRequest('/api/v1/wcl/kyc/business-owner/address', data);
    }

    getBOBusinessAddressInformation(data: any) {
        return this.makePostRequest('/api/v1/wcl/kyc/fetch-business-address', data);
    }

    confirmKYC(data: any) {
        return this.makePostRequest('/api/v1/wcl/kyc/confirm', data);
    }

    uploadFile(file) {
        const formData = new FormData();
        formData.append('file', file);
        return this.makeMultipartRequest('/api/v1/wcl/file/upload', formData);
    }

    getImageDataFromRef(imageRef: string) {
        return this.makeGetRequest(`/api/v1/wcl/file/image/${imageRef}`);
    }

    submitBusinessFinanceService(data: any) {
        return this.makePutRequest('/api/v1/wcl/request/business-finances/submit', data);
    }

    submitBuisnessEmployeeService(data: any) {
        return this.makePutRequest('/api/v1/wcl/request/business-employees/submit', data);
    }

    submitLoanAmountInformation(data: any) {
        return this.makePutRequest('/api/v1/wcl/request/amount/submit', data);
    }

    submitLoanRenewalAmountInformation(data: any) {
        return this.makePutRequest('/api/v1/wcl/renewal/amount/submit', data);
    }

    submitCombinedBusinessGuarantorsInformation(loanRequestRef: string, data: Record<string, any>) {
        return this.makePutRequest(`/api/v1/wcl/request/${encodeURIComponent(loanRequestRef)}/guarantors`, data);
    }

    submitBusinessGuarantorInformation(data: any) {
        return this.makePostRequest('/api/v1/wcl/request/guarantors/submit', data);
    }

    fetchBusinessGuarantorInformationForLoanRequest(params: any) {
        return this.makeGetRequest(`/api/v1/wcl/request/guarantor`, params, true);
    }

    updateBusinessGuarantorInformation(data: any, id: any) {
        return this.makePutRequest(`/api/v1/wcl/request/${id}/guarantors/update`, data);
    }

    getLoanRequestSubmissionStatusInformation(data: any) {
        return this.makePostRequest('/api/v1/wcl/request/submission', data);
    }

    getLoanRequestSummaryInformation(data: any) {
        return this.makePostRequest('/api/v1/wcl/request/summary', data);
    }

    getEligibilityStatus(data: any) {
        return this.makePostRequest('/api/v1/wcl/eligibility/status', data);
    }

    validateBVN(data: any) {
        return this.makePostRequest('/api/v1/wcl/bvn/validation', data);
    }

    getOccupations() {
        return this.makeGetRequest(`/api/v1/wcl/request/guarantor/occupations`);
    }

    getRelationships() {
        return this.makeGetRequest(`/api/v1/wcl/request/guarantor/relationships`);
    }

    submitLoanRequest(data: any) {
        return this.makePostRequest(`/api/v1/wcl/request/process`, data);
    }

    getMetaMapFlowId(data: any): Promise<any> {
        return this.makePostRequest(`/api/v1/wcl/verification/reference`, data);
    }

    getLoanOfferInformation(data: any) {
        return this.makePostRequest(`/api/v1/wcl/offer/details`, data).then((res) => {
            this.activeLoanProduct = res?.loanProduct;
            return res;
        });
    }

    getActiveLoanRequestParticipants() {
        return this.makeGetRequest(`/api/v1/wcl/request/participant`, {}, true);
    }
    getLoanRequestParticipantsByEmail(data: any) {
        return this.makeGetRequest(`/api/v1/lms/loan-participant/${data.email}`, {}, true);
    }

    getNoLoanOfferInformation(data: any) {
        return this.makePostRequest(`/api/v1/wcl/offer/none`, data);
    }

    getTACInformation() {
        return this.makeGetRequest(`/api/v1/wcl/request/guarantor/occupations`);
    }

    initiateLoanAcceptanceOffer(data: any): Promise<any> {
        return this.makePostRequest(`/api/v1/wcl/offer/decision`, data);
    }

    acceptOverdraftContract(data: any): Promise<any> {
        return this.makePostRequest(`/api/v1/overdraft/contract/accept`, data);
    }

    rejectOverdraftContract(data: any): Promise<any> {
        return this.makePostRequest(`/api/v1/overdraft/contract/reject`, data);
    }

    reapplyForWorkingCapitalLoanBasedOnType(data, isScratch) {
        if (isScratch) {
            return this.reapplyForAWorkingCapitalLoan(data);
        }
        return this.reapplyForAWorkingCapitalLoanRenewal(data);
    }

    reapplyForLoan(data: any): Promise<LoanRequestReApplicationResponse> {
        return this.makePostRequest(`/api/v1/loans/loan-request/reapply`, data);
    }

    reapplyForAWorkingCapitalLoan(data: any): Promise<any> {
        return this.makePostRequest(`/api/v1/wcl/request/reapply`, data);
    }

    reapplyForAWorkingCapitalLoanRenewal(data: any) {
        return this.makePostRequest(`/api/v1/wcl/renewal/reapply`, data);
    }

    checkMetamapVerificationStatus(data: any): Promise<any> {
        return this.makePostRequest(`/api/v1/wcl/metamap/verification-status`, data);
    }

    checkForActiveContract(data: any) {
        return this.makeGetRequest(`/api/v1/wcl/contract/active`).then((res) => {
            if (res?.available) {
                this.activeLoanProduct = res?.loanProduct;
            }
            return res;
        });
    }

    getActiveContractreference() {
        return this.makeGetRequest(`/api/v1/wcl/contract/active`, {}, true);
    }

    getLoanContractDetails(data: any): Promise<any> {
        return this.makePostRequest(`/api/v1/wcl/contract/details`, data);
    }

    getOverdraftContractDetails(contractReference: any): Promise<any> {
        contractReference = encodeURIComponent(contractReference);
        return this.makeOVDGetRequest(`/api/v2/overdraft/contract/${contractReference}`, {}, true);
    }

    getLoanContractRepaymentSchedule(data: any, params): Promise<any> {
        params = StringHelper.toUrlString(params);
        return this.makePostRequest(`/api/v1/wcl/contract/repayment-schedule?${params}`, data);
    }

    getLoanContractRepaymentHistory(data: any, params): Promise<any> {
        params = StringHelper.toUrlString(params);
        return this.makePostRequest(`/api/v1/wcl/contract/repayment-history?${params}`, data);
    }

    getLoanRequestApprovalStatus(data) {
        if (this.config.USE_PENDING_LOAN_TIMELINE_V2) {
            return this.makeGetRequest(`/api/v1/lms/request/approval-details`);
        }
        return this.makePostRequest(`/api/v1/wcl/request/ongoing-approval/details`, data);
    }

    fetchGuarantorsList(data) {
        return this.makePostRequest(`/api/v1/wcl/request/fetch-guarantors`, data);
    }

    fetchGuarantorsListForContract(data: any) {
        return this.makePostRequest(`/api/v1/wcl/contract/fetch-guarantors`, data);
    }

    getWclContractReference() {
        this.getActiveContractreference()
            .then((res) => {
                if (res?.wclContractReference && res?.available) {
                    this.wclContractReferenceSet(res?.wclContractReference);
                }
            })
            .catch((err) => {})
            .finally(() => {});
    }

    async getBORepaymentAccounts(useOutlets = false): Promise<WclBoAccount[]> {
        if (useOutlets || !this.useBORepaymentAccounts) {
            return this.walletService.getFundTransferOutlets().then((outlets) =>
                outlets.map((outlet) => ({
                    accountName: outlet.businessName,
                    accountNuban: outlet.accountNumber,
                    accountBalance: this.convertKoboToNaira(outlet.walletBalance),
                })),
            );
        }

        return this.makeGetRequest(`/api/v1/wcl/business-owner/repayment-accounts`);
    }

    async getBODisbursementAccounts(useOutlets = false): Promise<WclBoAccount[]> {
        if (useOutlets || !this.useBODisbursementAccounts) {
            return this.walletService.getFundTransferOutlets().then((outlets) =>
                outlets.map((outlet) => ({
                    accountName: outlet.businessName,
                    accountNuban: outlet.accountNumber,
                    accountBalance: this.convertKoboToNaira(outlet.walletBalance),
                })),
            );
        }

        return this.makeGetRequest(`/api/v1/wcl/business-owner/disbursement-accounts`);
    }

    convertKoboToNaira(value: number) {
        return !value ? value : value / 100;
    }

    disburseLoan(data: any) {
        return this.makePostRequest(`/api/v1/wcl/contract/disburse`, data);
    }

    repayLoan(data: any) {
        return this.makePostRequest(`/api/v1/wcl/contract/repayment`, data);
    }

    getContractRepaymentAmount(data: any) {
        return this.makePostRequest(`/api/v1/wcl/contract/repayment/amount`, data);
    }

    getInterestAccruedHistory(data: any, params) {
        params = StringHelper.toUrlString(params);
        return this.makePostRequest(`/api/v1/wcl/contract/accrued-interest-history?${params}`, data);
    }

    getLoansHistoryData(data: any, params) {
        return this.makeGetRequest(`/api/v1/wcl/contract/history`, params);
    }

    getThresholdAmount(params) {
        return this.makeGetRequest(`/api/v1/wcl/request/amount/threshold`, params, true);
    }

    async callGetThresholdAmount(params: any) {
        if (!this.thresholdAmount) {
            try {
                const data = await this.getThresholdAmount(params);
                this.thresholdAmountSubject.next(data);
                this.thresholdAmount = data;
            } catch (err) {
                this.thresholdAmountSubject.next(null);
            }
        } else {
            const data = await Promise.resolve(this.thresholdAmount);
            this.thresholdAmountSubject.next(data);
        }
    }

    checkBOForLoanRenewalRequest() {
        return this.makePostRequest(`/api/v1/wcl/renewal`);
    }

    createLoanRenewalRequest() {
        return this.makePostRequest('/api/v1/loans/renewal', { loanProduct: this.activeLoanProduct });
    }

    confirmBOAddresses(data: any) {
        return this.makePutRequest(`/api/v1/wcl/renewal/kyc/addresses`, data);
    }

    getPreviousLoanAmountOnRenewal() {
        return this.makeGetRequest(`/api/v1/wcl/renewal/previous/loan-amount`, {}, true);
    }

    getDaysLeft(targetDate: Date) {
        const presentDate = new Date();
        const differenceInTime = targetDate?.getTime() - presentDate?.getTime();
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

        if (differenceInDays > 0) {
            return `${differenceInDays} days left`;
        } else if (differenceInDays < 0) {
            return `${Math.abs(differenceInDays)} days ago`;
        } else {
            return 'Today!';
        }
    }

    betterFormattedDaysMessage(targetDate: Date, completedDate: Date) {
        const targetDate_ = moment(targetDate);
        const seconds = targetDate_.diff(completedDate, 'second');
        const minutes = targetDate_.diff(completedDate, 'minute');
        const hours = targetDate_.diff(completedDate, 'hour');
        const days = targetDate_.diff(completedDate, 'day');

        if (days > 0) {
            return `in the next ${days} days`;
        } else if (days < 0) {
            return `${Math.abs(days)} days late`;
        } else if (days === 0) {
            return 'Today!';
        } else if (hours > 0) {
            return `in the next ${hours} hours`;
        } else if (minutes > 0) {
            return `in the next ${minutes} minutes`;
        } else {
            return `in the next ${seconds} seconds`;
        }
    }

    returnLoansStateOverdueDays(targetDate: Date, completedDate: Date) {
        const timeDifference = targetDate?.getTime() - completedDate?.getTime();
        const seconds = Math.floor(timeDifference / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days < 0) {
            return `${Math.abs(days)} days`;
        } else if (hours < 0) {
            return `${Math.abs(hours)} hours`;
        } else if (minutes < 0) {
            return `${Math.abs(minutes)} minutes`;
        } else if (seconds < 0) {
            return `${Math.abs(seconds)} seconds`;
        } else {
            return '-';
        }
    }

    mockCall(success = true, data: any, timeout = 3000) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                if (success) {
                    resolve(data);
                } else {
                    reject(false);
                }
            }, timeout);
        });
    }

    wclErrorMessage(err: PortfolioManagementServiceError) {
        return err?.error?.error?.message || StringHelper.getErrorMessageHttpErrorResponse(err);
    }

    goToPage(path: string, extras?: NavigationBehaviorOptions) {
        return this.router.navigateByUrl(this.buildWclPath(path), extras);
    }

    goToEntryPoint() {
        return this.goToPage('/home');
    }

    goToProductSelectionPage(data: any = null) {
        return this.goToPage('/loan-product-selection', { state: { data } });
    }

    buildWclPath(fragment: string) {
        const _fragment = fragment.startsWith('/') ? fragment.slice(1) : fragment;
        return `${this.baseRoutePath}/working-capital-loans-v2/${_fragment}`;
    }

    goToDashboard() {
        if (this.parentModule === WclParentModule.BUSINESS_USER) {
            return this.router.navigateByUrl(`${this.baseRoutePath}/dashboard`);
        }

        return this.router.navigateByUrl(`${this.baseRoutePath}/dashboard/home`);
    }

    getAccruedHistory(contractReference: string, pageNumber: number = 0, pageSize: number = 10): Promise<AccruedHistoryResponse> {
        contractReference = encodeURIComponent(contractReference);
        return this.makeOVDGetRequest(
            `/api/v1/overdraft/${contractReference}/accrued-history?pageNumber=${pageNumber}&pageSize=${pageSize}`,
            {},
            true,
        );
    }

    // BRM SC RM LOAN REQUEST AND PORTFOLIO MANAGEMENT ENDPOINTS
    getParticipantCommissions() {
        return this.portfolioManagementService.getParticipantCommissions();
    }

    getParticipantCommissionsTransactions(params: Pageable<IncomeCommissionsTransactionsParams>) {
        return this.portfolioManagementService.getParticipantCommissionsTransactions(params);
    }

    getParticipantPayoutTransactionHistory(params: Pageable<PayoutTransactionsHistoryParams>) {
        return this.portfolioManagementService.getParticipantPayoutTransactionHistory(params);
    }

    getParticipantPayoutCashoutBalance() {
        return this.portfolioManagementService.getParticipantPayoutCashoutBalance();
    }

    getParticipantCashoutAccountNumber() {
        return this.portfolioManagementService.getParticipantCashoutAccountNumber();
    }

    participantCashoutAction(data: ParticipantCashout) {
        return this.portfolioManagementService.participantCashoutAction(data);
    }

    getPortfolioList(params: Pageable<PortfolioListParams>) {
        return this.portfolioManagementService.getPortfolioList(params);
    }

    getPortfolioListCount() {
        return this.portfolioManagementService.getPortfolioListCount();
    }

    getLoanRequestList(params: Pageable<LoanRequestListParams>) {
        return this.portfolioManagementService.getLoanRequestList(params);
    }

    getLoanRequestsListCount() {
        return this.portfolioManagementService.getLoanRequestsListCount();
    }

    getSingleLoanRequestData(loanReference: string) {
        return this.portfolioManagementService.getSingleLoanRequestData(loanReference);
    }

    getImageRef(imageRef: string) {
        return this.portfolioManagementService.getImageRef(imageRef);
    }

    getBRMsLinkedToSC() {
        return this.portfolioManagementService.getBRMsLinkedToSC();
    }

    getSCsLinkedToRM() {
        return this.portfolioManagementService.getSCsLinkedToRM();
    }

    getBRMsLinkedToRMs(code: string) {
        return this.portfolioManagementService.getBRMsLinkedToRMs(code);
    }

    /* prequalification request */
    getPrequalificationList(params: Pageable<PrequalificationListParams>) {
        return this.portfolioManagementService.getPrequalificationList(params);
    }

    generateRepaymentSchedule(businessOwnerId: string | number) {
        return this.portfolioManagementService.generateRepaymentSchedule(businessOwnerId);
    }

    /** Prequalification Lookup */
    getPrequalificationLookupHistory(params: Pageable<{}>) {
        return this.portfolioManagementService.getPrequalificationLookupHistory(params);
    }

    getPrequalificationLookupHistoryV2(params: Pageable<{}>) {
        return this.portfolioManagementService.getPrequalificationLookupHistoryV2(params);
    }

    getPrequalificationLookupDetails(requestRef: string) {
        return this.portfolioManagementService.getPrequalificationLookupDetails(requestRef);
    }

    getPrequalificationLookupDetailsV2(requestRef: string) {
        return this.portfolioManagementService.getPrequalificationLookupDetailsV2(requestRef);
    }

    getPrequalificationLookup(accountRef: string) {
        return this.portfolioManagementService.getPrequalificationLookup(accountRef);
    }

    getPrequalificationLookupV2(accountRef: string) {
        return this.portfolioManagementService.getPrequalificationLookupV2(accountRef);
    }

    /* WCL loan agreement apis */
    submitOfferDecision(data: SubmitLoanOfferDecision) {
        return this.makePostRequest(`/api/v1/wcl/request/${data.loanRequestRef}/final-offer/decision`, data);
    }

    getOfferRejectionReasons(): Promise<RejectionReasonsResponse> {
        return this.makeGetRequest(`/api/v1/wcl/offer-decline-reasons`);
    }

    getFinalOfferDetails(loanRequestRef: string): Promise<FinalOfferDetailsResponse> {
        loanRequestRef = encodeURIComponent(loanRequestRef);
        if (this.useOVDNewAgreementFlow && (this.activeLoanProduct === LoanProduct.OVERDRAFT || this.activeLoanProduct === LoanProduct.AGENT_LOAN)) {
            return this.makeGetRequest(`/api/v1/loans/request/${loanRequestRef}/final-offer`);
        }
        return this.makeGetRequest(`/api/v1/wcl/request/${loanRequestRef}/final-offer`);
    }

    getWclLoanGuarantorAgreements(loanRequestRef: string): Promise<LoanGuarantorAgreementsResponse> {
        loanRequestRef = encodeURIComponent(loanRequestRef);
        return this.makeGetRequest(`/api/v1/wcl/request/${loanRequestRef}/guarantor-agreements`);
    }

    getDisbursementAccounts(loanRequestRef: string) {
        loanRequestRef = encodeURIComponent(loanRequestRef);
        return this.makeGetRequest(`/api/v1/wcl/request/${loanRequestRef}/disbursement-accounts`);
    }

    submitDisbursementAccount(data: any, loanRequestRef: string) {
        loanRequestRef = encodeURIComponent(loanRequestRef);
        return this.makePutRequest(`/api/v1/wcl/request/${loanRequestRef}/disbursement-accounts`, data);
    }

    getBorrowerLoanAgreement(loanRequestRef: string): Promise<BorrowerLoanAgreement> {
        loanRequestRef = encodeURIComponent(loanRequestRef);
        return this.makeGetRequest(`/api/v1/wcl/request/${loanRequestRef}/borrower-agreement`);
    }

    /* Provisinal Offers */
    submitPrimaryBusiness(data: PrimaryBusinessPayload) {
        return this.makePostRequest('/api/v1/wcl/primary-business', data);
    }

    submitPrimaryBusinessV2(data: PrimaryBusinessPayload) {
        return this.makePostRequest('/api/v1/loans/primary-business', data);
    }

    submitOVDPrimaryBusiness(data: PrimaryBusinessPayload) {
        return this.makePostRequest('/api/v1/loans/primary-business', data);
    }

    submitProvisionalOfferInfo(data, loanRequestReference) {
        const loanProduct = this.activeLoanProduct;

        // For OVD and AGL, use the loans endpoint
        if (loanProduct === LoanProduct.OVERDRAFT || loanProduct === LoanProduct.AGENT_LOAN) {
            const payload = {
                amount: data.amount,
                loanPurpose: data.loanPurpose,
                loanProduct: loanProduct,
            };
            return this.makePostRequest(`/api/v1/loans/provisional-offer/request`, payload);
        }

        // For WCL, use the existing wcl endpoint
        return this.makePostRequest(`/api/v1/wcl/request/${encodeURIComponent(loanRequestReference)}/provisional-offer`, data);
    }

    submitProvisionalOfferInfoV2(data) {
        const loanProduct = this.activeLoanProduct;
        const wclPayload = {
            ...data,
            loanProduct: loanProduct,
        };
        const ovdAglPayload = {
            amount: data.amount,
            loanPurpose: data.loanPurpose,
            loanProduct: loanProduct,
            ...(data.loanRequestRef && { loanRequestRef: data.loanRequestRef }),
        };
        const payload = loanProduct === LoanProduct.OVERDRAFT || loanProduct === LoanProduct.AGENT_LOAN ? ovdAglPayload : wclPayload;
        return this.makePostRequest(`/api/v1/loans/provisional-offer/request`, payload);
    }

    getProvisionalOfferConfigs() {
        return this.makeGetRequest('/api/v1/wcl/request/new').then((res) => {
            if (this.activeLoanProduct === LoanProduct.OVERDRAFT) {
                return {
                    validation: {
                        ...res.validation,
                        amount: {
                            min: 50000000,
                            max: 100000000,
                        },
                    },
                };
            }

            return res;
        });
    }

    getProvisionalOfferStatus(loanRequestRef: string) {
        const loanProduct = this.activeLoanProduct;

        if (loanProduct === LoanProduct.OVERDRAFT || loanProduct === LoanProduct.AGENT_LOAN) {
            return this.makeGetRequest(`/api/v1/loans/provisional-offer/request/${encodeURIComponent(loanRequestRef)}`);
        }

        return this.makeGetRequest(`/api/v1/wcl/request/${encodeURIComponent(loanRequestRef)}/provisional-offer`);
    }

    getLoanRequestPrefillData(loanRequestRef: string): Promise<LoanPrefillData> {
        return this.makeGetRequest(`/api/v1/wcl/request/${encodeURIComponent(loanRequestRef)}/provisional-offer-requests`);
    }

    submitProvisionalOfferAcceptance(loanRequestRef: string, data) {
        return this.makePostRequest(`/api/v1/wcl/request/${encodeURIComponent(loanRequestRef)}/provisional-offer/decision`, data);
    }

    getOVDOutstandingLoanAmount(contractReference: any): Promise<any> {
        contractReference = encodeURIComponent(contractReference);
        return this.makeOVDGetRequest(`/api/v1/overdraft/contract/${contractReference}/outstanding-balance`, {}, true);
    }

    getOVDRepaymentAccounts(contractReference): Promise<any> {
        contractReference = encodeURIComponent(contractReference);
        return this.makeOVDGetRequest(`/api/v1/overdraft/contract/${contractReference}/repayment-accounts`, {}, true);
    }

    validateTransactionPin(data: any) {
        return this.makeOVDPostRequest(`/lms/business-owner/pin-validation`, data);
    }

    submitOVDManualRepayment(data: any) {
        return this.makeOVDPostRequest(`/api/v1/overdraft/contract/manual-repayment`, data);
    }

    getLoanProducts(): Promise<any> {
        return this.makeLMSGetRequest(`/api/v1/lms/business-owner/loan-products`, {}, true);
    }
    // ----------------
    // helper methods
    // ----------------
    private makeGetRequest(url: string, params: object = {}, checkAuth = false) {
        url = this.buildWCLMSRequestUrl(url);
        return this.apiClientHelper.getForPromise(url, checkAuth, false, false, this.customHeaders, params);
    }

    private makeOVDGetRequest(url: string, params: object = {}, checkAuth = false) {
        url = this.buildOVDRequestUrl(url);
        return this.apiClientHelper.getForPromise(url, checkAuth, false, false, this.customHeaders, params);
    }

    private makeLMSGetRequest(url: string, params: object = {}, checkAuth = false) {
        url = this.buildLMSRequestUrl(url);
        return this.apiClientHelper.getForPromise(url, checkAuth, false, false, this.customHeaders, params);
    }

    private makeOVDPostRequest(url: string, data: object = {}) {
        url = this.buildOVDRequestUrl(url);
        return this.apiClientHelper.postForPromise(url, data, true, false, false, 'WEB', this.customHeaders);
    }

    private makePostRequest(url: string, data: object = {}) {
        url = this.buildWCLMSRequestUrl(url);
        return this.apiClientHelper.postForPromise(url, data, true, false, false, 'WEB', this.customHeaders);
    }

    private makeMultipartRequest(url: string, formData: FormData) {
        url = this.buildWCLMSRequestUrl(url);
        return this.apiClientHelper.postForPromiseMultipart(url, formData, true, false, 'WEB', this.customHeaders);
    }

    private makePutRequest(url: string, data: object = {}) {
        url = this.buildWCLMSRequestUrl(url);
        return this.apiClientHelper.putForPromise(url, data, false, false, 'WEB', this.customHeaders);
    }

    buildWCLMSRequestUrl(path) {
        return this.config.WLC_MANAGEMENT + path;
    }

    // TODO : - OVD_SERVICE needs to be configured on Staging and Production too
    buildOVDRequestUrl(path) {
        return `${this.config.LOAN_GATEWAY_SERVICE_URL}/ovd` + path;
    }

    buildLMSRequestUrl(path) {
        return `${this.config.LOAN_GATEWAY_SERVICE_URL}/lms` + path;
    }

    buildMOCKRequestUrl(path) {
        const url = mockUrl + path;
        return url;
    }

    getCurrentLoanRequestState(): LoanRequestState {
        const ongoingLoan = localStorage.getItem(StorageKeys.ONGOING_LOAN_DATA);
        return JSON.parse(ongoingLoan);
    }

    getProductLabel(product: string): string {
        return loanProductToLabelMap[product] || 'Working Capital Loan';
    }

    setActiveLoanProduct(product: string) {
        this.activeLoanProduct = product;
    }

    saveLoanRequestReference(ref: string | null) {
        localStorage.setItem(StorageKeys.loanRequestRef, ref ?? '');
    }

    get loanRequestReference() {
        return localStorage.getItem(StorageKeys.loanRequestRef) ?? null;
        // defaulting to null to ensure it defaults to history?.state when it's not in storage
    }

    get useUnifiedLoanRequestFlow() {
        return this.config.USE_UNIFIED_PROVISIONAL_OFFER_FLOW && this.config.SHOW_LOAN_PRODUCT_SELECTION_FLOW;
    }

    get useOVDNewAgreementFlow() {
        return this.config.USE_OVD_NEW_AGREEMENT_FLOW;
    }

    get wclRequestMode() {
        return sessionStorage.getItem(StorageKeys.WCL_REQUEST_MODE);
    }

    get baseRoutePath() {
        return `/main-app/${this.parentModule || WclParentModule.OUTLET_MANAGER}`;
    }

    get activeLoanProductLabel(): string {
        return loanProductToLabelMap[this.activeLoanProduct] || 'Working Capital Loan';
    }

    get activeLoanProduct() {
        return localStorage.getItem(StorageKeys.SELECTED_LOAN_PRODUCT) || LoanProduct.WCL;
    }

    set activeLoanProduct(value) {
        // defaulting to 'WCL' to cater for select loan product flag turned off
        this.localStorage.save(StorageKeys.SELECTED_LOAN_PRODUCT, value || LoanProduct.WCL);
    }

    get isWCL(): boolean {
        return this.activeLoanProduct === LoanProduct.WCL;
    }
}

const mockUrl = 'http://localhost:3001';
